import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from "react";

import { toast } from "react-toastify";
import { useParams, useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import BlockIcon from "@material-ui/icons/Block";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListItemModal from "../../components/ContactListItemModal";
import ConfirmationModal from "../../components/ConfirmationModal/";
import ContactListImportProgress from "../../components/ContactListImportProgress";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import useContactLists from "../../hooks/useContactLists";
import { Grid } from "@material-ui/core";

import planilhaExemplo from "../../assets/planilha.xlsx";
import { SocketContext } from "../../context/Socket/SocketContext";

// O upload só grava os itens (a validação no WhatsApp roda em segundo plano),
// mas uma planilha grande em conexão lenta pode levar mais que os 30 s padrão.
const UPLOAD_TIMEOUT_MS = 120000;
// Sem eventos de validação por este tempo (ex.: backend reiniciou), a barra
// some; os itens pendentes continuam com "?". Na fila, a espera por outra lista
// da empresa pode ser longa.
const VALIDATION_STALE_MS = { running: 180000, queued: 30 * 60 * 1000 };

const notifyValidationDone = (data) => {
  if (!data.total) return;

  const counts = {
    valid: data.valid || 0,
    invalid: data.invalid || 0,
    unvalidated: data.unvalidated || 0,
  };

  if (data.status === "done") {
    toast.success(
      i18n.t(
        counts.unvalidated > 0
          ? "contactListItems.validation.doneWithPending"
          : "contactListItems.validation.done",
        counts
      )
    );
  } else if (data.status === "unavailable") {
    toast.warning(i18n.t("contactListItems.validation.unavailable"));
  } else if (data.status === "error") {
    toast.error(i18n.t("contactListItems.validation.error"));
  }
};

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      // Contato já existe na lista - atualiza
      state[contactIndex] = contact;
      return [...state];
    } else {
      // Contato novo via socket: adiciona apenas se pertencer à lista aberta
      // e corresponder ao filtro de busca atual.
      const currentContactListId = Number(action.contactListId);
      if (Number(contact.contactListId) !== currentContactListId) {
        return state;
      }

      const term = (action.searchParam || "").trim().toLowerCase();
      if (!term) {
        return [contact, ...state];
      }

      const matches =
        String(contact.name || "").toLowerCase().includes(term) ||
        String(contact.number || "").toLowerCase().includes(term) ||
        String(contact.email || "").toLowerCase().includes(term);

      return matches ? [contact, ...state] : state;
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "PATCH_CONTACTS") {
    // Resultado da validação: só atualiza linhas já na tela (paginação/busca)
    const patches = new Map(action.payload.map((item) => [item.id, item]));
    let changed = false;
    const next = state.map((contact) => {
      const patch = patches.get(contact.id);
      if (!patch) return contact;
      changed = true;
      return { ...contact, ...patch };
    });
    return changed ? next : state;
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const ContactListItems = () => {
  const classes = useStyles();

  const { user } = useContext(AuthContext);
  const { contactListId } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactListItemModalOpen, setContactListItemModalOpen] =
    useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [contactList, setContactList] = useState({});
  const [uploading, setUploading] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [validation, setValidation] = useState(null);
  const fileUploadRef = useRef(null);
  const uploadingRef = useRef(false);
  const importIdRef = useRef(null);
  const lastValidationEventRef = useRef(0);
  const validationStatusRef = useRef(null);

  const { findById: findContactList } = useContactLists();

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    findContactList(contactListId).then((data) => {
      setContactList(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactListId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get(`contact-list-items`, {
            params: { searchParam, pageNumber, contactListId },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, contactListId]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const handleContactListItem = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({
          type: "UPDATE_CONTACTS",
          payload: data.record,
          searchParam,
          contactListId,
        });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.id });
      }

      if (data.action === "reload") {
        dispatch({ type: "LOAD_CONTACTS", payload: data.records });
      }
    };

    socket.on(`company-${companyId}-ContactListItem`, handleContactListItem);

    return () => {
      socket.disconnect();
    };
  }, [contactListId, searchParam, socketManager]);

  // Canal da lista: itens importados, progresso da importação (só desta aba)
  // e da validação no WhatsApp em segundo plano.
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    setValidation(null);
    validationStatusRef.current = null;

    const handleContactListEvent = (data) => {
      if (data.action === "reload") {
        dispatch({ type: "LOAD_CONTACTS", payload: data.records || [] });
      }

      if (data.action === "import-progress") {
        if (
          uploadingRef.current &&
          data.importId &&
          data.importId === importIdRef.current
        ) {
          setImportProgress({
            stage: "import",
            processed: data.processed || 0,
            total: data.total || null,
          });
        }
      }

      if (data.action === "validation-progress") {
        lastValidationEventRef.current = Date.now();
        validationStatusRef.current = data.status;
        if (Array.isArray(data.items) && data.items.length > 0) {
          dispatch({ type: "PATCH_CONTACTS", payload: data.items });
        }
        setValidation({
          status: data.status,
          checked: data.checked || 0,
          total: data.total || 0,
        });
      }

      if (data.action === "validation-done") {
        validationStatusRef.current = null;
        setValidation(null);
        notifyValidationDone(data);
      }
    };

    socket.on(
      `company-${companyId}-ContactListItem-${contactListId}`,
      handleContactListEvent
    );

    return () => {
      socket.disconnect();
    };
  }, [contactListId, socketManager]);

  const validationActive = Boolean(validation);

  useEffect(() => {
    if (!validationActive) return undefined;

    const timer = setInterval(() => {
      const limit =
        VALIDATION_STALE_MS[validationStatusRef.current] ||
        VALIDATION_STALE_MS.running;
      if (Date.now() - lastValidationEventRef.current > limit) {
        validationStatusRef.current = null;
        setValidation(null);
      }
    }, 15000);

    return () => clearInterval(timer);
  }, [validationActive]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(true);
  };

  const handleCloseContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactListItemModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contact-list-items/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleImportContacts = async () => {
    const file = fileUploadRef.current?.files?.[0];
    if (uploadingRef.current || !file) return;

    // Identifica este upload nos eventos de progresso do socket
    const importId = `${Date.now().toString(36)}${Math.random()
      .toString(36)
      .slice(2, 10)}`;
    importIdRef.current = importId;
    uploadingRef.current = true;
    setUploading(true);
    setImportProgress({ stage: "upload", percent: 0 });

    try {
      const formData = new FormData();
      formData.append("importId", importId);
      formData.append("file", file);

      const { data } = await api.request({
        url: `contact-lists/${contactListId}/upload`,
        method: "POST",
        data: formData,
        timeout: UPLOAD_TIMEOUT_MS,
        onUploadProgress: (event) => {
          const ratio =
            event.progress ?? (event.total ? event.loaded / event.total : 0);
          setImportProgress((current) => {
            if (!current || current.stage !== "upload") return current;
            return ratio < 1
              ? { stage: "upload", percent: Math.round(ratio * 100) }
              : { stage: "import", processed: 0, total: null };
          });
        },
      });

      toast.success(
        i18n.t("contactListItems.toasts.imported", {
          created: data?.created || 0,
          duplicates: data?.duplicates || 0,
          invalid: data?.invalid || 0,
        })
      );
    } catch (err) {
      toastError(err);
    } finally {
      uploadingRef.current = false;
      importIdRef.current = null;
      setUploading(false);
      setImportProgress(null);
      if (fileUploadRef.current) {
        fileUploadRef.current.value = null;
      }
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const goToContactLists = () => {
    history.push("/contact-lists");
  };

  let progressLabel = null;
  let progressValue = null;
  if (importProgress?.stage === "upload") {
    progressLabel = i18n.t("contactListItems.importProgress.uploading", {
      percent: importProgress.percent,
    });
    progressValue = importProgress.percent;
  } else if (importProgress?.stage === "import") {
    if (importProgress.total) {
      progressLabel = i18n.t("contactListItems.importProgress.importing", {
        processed: importProgress.processed,
        total: importProgress.total,
      });
      progressValue = (importProgress.processed / importProgress.total) * 100;
    } else {
      progressLabel = i18n.t("contactListItems.importProgress.processing");
    }
  } else if (validation) {
    if (validation.status === "running" && validation.total > 0) {
      progressLabel = i18n.t("contactListItems.validation.progress", {
        checked: validation.checked,
        total: validation.total,
      });
      progressValue = (validation.checked / validation.total) * 100;
    } else {
      progressLabel = i18n.t("contactListItems.validation.queued");
    }
  }

  return (
    <MainContainer className={classes.mainContainer}>
      <ContactListItemModal
        open={contactListItemModalOpen}
        onClose={handleCloseContactListItemModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      ></ContactListItemModal>
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contactListItems.confirmationModal.deleteTitle")} ${
                deletingContact.name
              }?`
            : `${i18n.t("contactListItems.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() =>
          deletingContact
            ? handleDeleteContact(deletingContact.id)
            : handleImportContacts()
        }
      >
        {deletingContact ? (
          `${i18n.t("contactListItems.confirmationModal.deleteMessage")}`
        ) : (
          <>
            {i18n.t("contactListItems.confirmationModal.importMessage")}
            <a href={planilhaExemplo} download="planilha.xlsx">
              {i18n.t("contactListItems.confirmationModal.downloadSample")}
            </a>
          </>
        )}
      </ConfirmationModal>
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container>
          <Grid xs={12} sm={5} item>
            <Title>{contactList.name}</Title>
          </Grid>
          <Grid xs={12} sm={7} item>
            <Grid spacing={2} container>
              <Grid xs={12} sm={6} item>
                <TextField
                  fullWidth
                  placeholder={i18n.t("contactListItems.searchPlaceholder")}
                  type="search"
                  value={searchParam}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: "gray" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid xs={4} sm={2} item>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={goToContactLists}
                >
                  {i18n.t("contactListItems.buttons.lists")}
                </Button>
              </Grid>
              <Grid xs={4} sm={2} item>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={uploading}
                  onClick={() => {
                    fileUploadRef.current.value = null;
                    fileUploadRef.current.click();
                  }}
                >
                  {uploading
                    ? i18n.t("contactListItems.buttons.importing")
                    : i18n.t("contactListItems.buttons.import")}
                </Button>
              </Grid>
              <Grid xs={4} sm={2} item>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleOpenContactListItemModal}
                >
                  {i18n.t("contactListItems.buttons.add")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MainHeader>
      {progressLabel && (
        <ContactListImportProgress label={progressLabel} value={progressValue} />
      )}
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <>
          <input
            style={{ display: "none" }}
            id="upload"
            name="file"
            type="file"
            accept=".xls,.xlsx"
            onChange={() => {
              // Um "Excluir" cancelado antes deixava deletingContact preenchido
              // e o modal abria em modo exclusão, apagando o contato.
              setDeletingContact(null);
              setConfirmOpen(true);
            }}
            ref={fileUploadRef}
          />
        </>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center" style={{ width: "0%" }}>
                #
              </TableCell>
              <TableCell>{i18n.t("contactListItems.table.name")}</TableCell>
              <TableCell align="center">
                {i18n.t("contactListItems.table.number")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("contactListItems.table.email")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("contactListItems.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell align="center" style={{ width: "0%" }}>
                    <IconButton>
                      {contact.isWhatsappValid === true ? (
                        <CheckCircleIcon
                          titleAccess={i18n.t("contactListItems.table.validWhatsapp")}
                          htmlColor="green"
                        />
                      ) : contact.isWhatsappValid === false ? (
                        <BlockIcon
                          titleAccess={i18n.t("contactListItems.table.invalidWhatsapp")}
                          htmlColor="grey"
                        />
                      ) : (
                        <HelpOutlineIcon
                          titleAccess={i18n.t("contactListItems.table.notValidated")}
                          htmlColor="orange"
                        />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>{contact.name}</TableCell>
                  <TableCell align="center">{contact.number}</TableCell>
                  <TableCell align="center">{contact.email}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => hadleEditContact(contact.id)}
                    >
                      <EditIcon />
                    </IconButton>
                    <Can
                      role={user.profile}
                      perform="contacts-page:deleteContact"
                      yes={() => (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setConfirmOpen(true);
                            setDeletingContact(contact);
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default ContactListItems;
